import { useEffect, useMemo, useRef } from 'react'
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import RowInput from '../components/form/RowInput'
import EmailInputWithDomain from '../components/form/EmailInputWithDomain'
import AddAnotherButton from '../components/form/AddAnotherButton'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import Band from '../components/form/Band'
import StepLayout from '../components/form/StepLayout'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { extractEmailDomain, ordinalLabel, personalizeTitle } from '../lib/formatters'
import {
  flattenErrorCount,
  isValidEmail,
  isValidPhone,
  suggestEmailCorrection,
  validateName,
} from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'

const STEP_ID = 'owners'

const strictSchema = z
  .object({
    owners: z
      .array(
        z.object({
          fullName: z.string().refine((v) => validateName(v).isValid, {
            message: 'Add a full name (first and last)',
          }),
          phoneNo: z.string().refine(isValidPhone, {
            message: 'Phone must be 10 digits',
          }),
          email: z.string().refine(isValidEmail, {
            message: 'Add a work email',
          }),
        }),
      )
      .min(1, 'Add at least one owner'),
  })
  .superRefine((data, ctx) => {
    const seen = new Map()
    data.owners.forEach((o, i) => {
      const key = String(o?.email ?? '').trim().toLowerCase()
      if (!key) return
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['owners', i, 'email'],
          message: 'Each owner needs a unique email',
        })
      } else {
        seen.set(key, i)
      }
    })
  })
const looseSchema = z.object({
  owners: z.array(z.any()).optional(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const emptyOwner = { fullName: '', phoneNo: '', email: '' }

export default function Step3Owners() {
  const step = STEPS.find((s) => s.id === 'owners')
  const practiceName = useOnboardingStore(
    (s) => s.formData?.details?.practiceName,
  )
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const practiceWebsite = useOnboardingStore(
    (s) => s.formData?.details?.website,
  )
  const suggestedDomain =
    suggested?.emailDomain || extractEmailDomain(practiceWebsite) || null
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  const defaultValues = useMemo(
    () => ({
      owners:
        persisted?.owners && persisted.owners.length > 0
          ? persisted.owners
          : [emptyOwner],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const { fields, append, remove } = useFieldArray({ control, name: 'owners' })
  const watched = useWatch({ control, name: 'owners' })

  // Card-on-file moved to the Payment & Labs (billing) step — Owners is now
  // purely about ownership. See Step2Billing for the card verification flow.
  const onValid = (data) => {
    updateFormData(STEP_ID, { owners: data.owners })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  // Keep refs so the registered handler always calls the latest callbacks.
  const onValidRef = useRef(onValid)
  onValidRef.current = onValid
  const onInvalidRef = useRef(onInvalid)
  onInvalidRef.current = onInvalid

  useEffect(() => {
    const submit = handleSubmit(
      (...args) => onValidRef.current(...args),
      (...args) => onInvalidRef.current(...args),
    )
    const token = setHandler(() => submit())
    return () => clearHandler(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // One-time phone pre-fill from enrichment suggestion if Owner 1 phone empty.
    if (suggested?.phone) {
      const cur = String(getValues('owners.0.phoneNo') || '').trim()
      if (!cur) {
        setValue('owners.0.phoneNo', suggested.phone, {
          shouldDirty: false,
          shouldValidate: false,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <form
      className={`step-body step-body--owners${liteMode ? ' is-lite-owners' : ''}`}
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
    >
      <StepLayout
        question={personalizeTitle('Who owns {practice}?', { practiceName })}
        helper={step.subtitle}
      >
      <ErrorBanner show={submitAttempted} messages={errorMessages} />
      <Band label="Practice owners" description="Who owns and operates this practice">
      <div className="owner-cards">
        {fields.map((f, idx) => {
          const rowErr = errors?.owners?.[idx]
          const rowTouched = touchedFields?.owners?.[idx] || {}
          const rowDirty = dirtyFields?.owners?.[idx] || {}
          const fieldHasError = (field) => {
            const err = rowErr?.[field]
            if (!err || !err.message) return false
            return !!rowTouched?.[field] || !!rowDirty?.[field]
          }
          const fullNameError = fieldHasError('fullName') ? rowErr.fullName : null
          const phoneError = fieldHasError('phoneNo') ? rowErr.phoneNo : null
          const emailError = fieldHasError('email') ? rowErr.email : null
          const emailVal = watched?.[idx]?.email
          const emailSuggestion = suggestEmailCorrection(emailVal)
          const label = ordinalLabel(idx, 'owner', 'Primary contact')
          const removeAction =
            fields.length > 1 ? (
              <button
                type="button"
                className="form-card-remove"
                aria-label="Remove this owner"
                onClick={() => remove(idx)}
              >
                Remove
              </button>
            ) : null
          return (
            <Card key={f.id} title={label} actions={removeAction}>
              <FieldRow cols={2}>
                <Field label="Full name" error={fullNameError}>
                  <RowInput
                    type="text"
                    placeholder="Full legal name"
                    autoComplete="name"
                    error={!!fullNameError}
                    {...register(`owners.${idx}.fullName`)}
                  />
                </Field>
                <Field label="Phone" error={phoneError}>
                  <Controller
                    name={`owners.${idx}.phoneNo`}
                    control={control}
                    render={({ field }) => (
                      <RowInput
                        type="phone"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="(123) 456-7890"
                        autoComplete="tel"
                        error={!!phoneError}
                      />
                    )}
                  />
                </Field>
              </FieldRow>
              <FieldRow cols={1}>
                <Field
                  label="Email"
                  error={emailSuggestion ? null : emailError}
                >
                  <Controller
                    name={`owners.${idx}.email`}
                    control={control}
                    render={({ field }) => (
                      <EmailInputWithDomain
                        value={field.value}
                        onChange={field.onChange}
                        suggestedDomain={suggestedDomain}
                        placeholder={suggestedDomain ? `you@${suggestedDomain}` : 'Work email'}
                        autoComplete="email"
                        error={!!emailError && !emailSuggestion}
                      />
                    )}
                  />
                  {emailSuggestion && (
                    <div className="email-suggestion">
                      Did you mean{' '}
                      <button
                        type="button"
                        className="email-suggestion-accept"
                        onClick={() =>
                          setValue(
                            `owners.${idx}.email`,
                            emailSuggestion,
                            { shouldValidate: true, shouldDirty: true },
                          )
                        }
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </div>
                  )}
                </Field>
              </FieldRow>
            </Card>
          )
        })}
      </div>

      <AddAnotherButton
        label="Add another owner"
        onClick={() => append({ ...emptyOwner })}
      />
      </Band>
      </StepLayout>

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
